import { createRouter, createWebHistory } from "vue-router";
import { usePersistentStore } from "@/store";
import authStore from "@/store/auth";
import { authManager } from "@/services/authManager";
import publicRoutes from "./publicRoutes";
import { setRouterInstance, addDynamicRoutes, refreshRoutes, setDynamicRoutesResetHandler } from "./routeManager";

const routes = [...publicRoutes];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Set the router instance for the route manager
setRouterInstance(router);

let adminRoutesAdded = false;
let authenticatedRoutesAdded = false;

setDynamicRoutesResetHandler(() => {
  adminRoutesAdded = false;
  authenticatedRoutesAdded = false;
});

const addAuthenticatedRoutes = async () => {
  if (authenticatedRoutesAdded) return;

  const authenticatedRoutes = (await import("./authenticatedRoutes")).default;
  addDynamicRoutes(authenticatedRoutes);

  authenticatedRoutesAdded = true;
};

const addAdminRoutes = async () => {
  if (adminRoutesAdded) return;

  const adminRoutes = (await import("./adminRoutes")).default;
  addDynamicRoutes(adminRoutes);

  adminRoutesAdded = true;
};

const isCatchAllPath = (path) => {
  return path === "/:pathMatch(.*)";
};

export const routeAccessRedirect = (to, { isAdmin, isSuperAdmin }) => {
  if (to.matched.some((record) => record.meta?.requiresSuperAdmin) && !isSuperAdmin) return "/";
  if (to.matched.some((record) => record.meta?.requiresAdmin) && !isAdmin) return "/";
  return null;
};

router.beforeEach(async (to, _from, next) => {
  if (authManager.isProcessing) {
    await authManager.wait();
  }

  const isPublicRoute = to.matched.some((record) => record.meta.public);
  const isCatchAllRoute = to.matched.some((record) => isCatchAllPath(record.path));

  if (isCatchAllRoute && !isPublicRoute) {
    if (authStore.getIsAuthenticated()) {
      await addAuthenticatedRoutes();
      if (authStore.isAdmin.value) {
        await addAdminRoutes();
      }

      try {
        const resolved = router.resolve(to.path);
        const routeExists =
          resolved.matched.length > 0 && !resolved.matched.some((record) => isCatchAllPath(record.path));
        if (routeExists) {
          return next({ ...to, replace: true });
        }
      } catch {}
    } else {
      return next("/login");
    }
  }

  if (!isPublicRoute && !isCatchAllRoute) {
    if (!authStore.getIsAuthenticated()) {
      return next("/login");
    }

    await addAuthenticatedRoutes();

    if (authStore.isAdmin.value) {
      await addAdminRoutes();
    }
  }

  const accessRedirect = routeAccessRedirect(to, {
    isAdmin: authStore.isAdmin.value,
    isSuperAdmin: authStore.isSuperAdmin.value,
  });
  if (accessRedirect) return next(accessRedirect);

  next();
});

router.afterEach((to) => {
  if (to.path.startsWith("/search-")) {
    const store = usePersistentStore();
    store.app.states.searchView = to.path;
  }
});

export const ensureRoutesUpToDate = async () => {
  if (!authStore.getIsAuthenticated()) {
    return;
  }

  await addAuthenticatedRoutes();
  if (authStore.isAdmin.value) {
    await addAdminRoutes();
  }
  refreshRoutes();
};

export default router;
